import { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import { fetchStudentGroups, StudentGroup } from '../api/my-teachers-api';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';

export const StudentGroupContext = createContext<{
  groups: Record<string, StudentGroup[]>;
  loading: boolean;
  refetch: (schoolYear: string) => Promise<void>;
}>({
  groups: {},
  loading: true,
  refetch: () => Promise.resolve(),
});

export default function StudentGroupContextProvider({
  children,
  schoolYear,
}: {
  children: ReactNode;
  schoolYear: string;
}) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<Record<string, StudentGroup[]>>({});
  const handleError = useThrowAsyncError();

  /**
   * Refetch the groups for a given school year. This should not cause a loading screen.
   * Used after accepting an invitation.
   */
  const refetch = useCallback(
    async (schoolYear: string) => {
      try {
        const groups = await fetchStudentGroups(schoolYear);

        setGroups(prev => ({ ...prev, [schoolYear]: groups }));
      } catch (error) {
        handleError(error);
      }
    },
    [handleError],
  );

  useEffect(() => {
    let current = true;

    const getGroups = async () => {
      if (!schoolYear) return;

      setLoading(true);

      try {
        if (!groups[schoolYear]) {
          const groups = await fetchStudentGroups(schoolYear);
          if (current) setGroups(prev => ({ ...prev, [schoolYear]: groups }));
        }
      } catch (error) {
        handleError(error);
      } finally {
        if (current) setLoading(false);
      }
    };

    getGroups();

    return () => {
      current = false;
    };
  }, [schoolYear, handleError, groups]);

  return <StudentGroupContext.Provider value={{ groups, loading, refetch }}>{children}</StudentGroupContext.Provider>;
}
